/**
 * Shared ECLE stage view builder for timeline + pursuit rail.
 * Keep in sync with commercial-lifecycle context semantics.
 */
import type {
  CommercialLifecycleContext,
  CommercialLifecycleDefinition,
  CommercialLifecycleStageDef,
} from '@/services/commercialLifecycle.service';

export type LifecycleStageStatus =
  | 'completed'
  | 'current'
  | 'pending'
  | 'optional'
  | 'blocked'
  | 'approval_pending';

export type LifecycleStageView = {
  stageKey: string;
  label: string;
  status: LifecycleStageStatus;
  isOptional: boolean;
  requiresApproval: boolean;
  entityType?: string;
};

export function humanizeStageKey(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export function lifecycleStatusGlyph(status: LifecycleStageStatus): string {
  switch (status) {
    case 'completed':
      return '✓';
    case 'current':
    case 'approval_pending':
      return '●';
    case 'blocked':
      return '✕';
    default:
      return '○';
  }
}

export function lifecycleStatusColor(status: LifecycleStageStatus): string {
  switch (status) {
    case 'completed':
      return '#52c41a';
    case 'current':
      return '#1677ff';
    case 'approval_pending':
      return '#fa8c16';
    case 'blocked':
      return '#ff4d4f';
    case 'optional':
      return '#8c8c8c';
    default:
      return '#bfbfbf';
  }
}

export function buildLifecycleStages(
  ctx: CommercialLifecycleContext,
  definition: CommercialLifecycleDefinition | undefined
): LifecycleStageView[] {
  const stages: CommercialLifecycleStageDef[] =
    definition?.stages?.slice().sort((a, b) => a.sortOrder - b.sortOrder) ?? [];

  if (!stages.length) {
    const fallbackKeys = [
      ...(ctx.lineage?.entities || []).map((e) => e.stageKey),
      ctx.currentStage?.stageKey,
      ...(ctx.nextStages || []).map((s) => s.stageKey),
    ].filter(Boolean) as string[];
    const unique = Array.from(new Set(fallbackKeys));
    return unique.map((stageKey, idx) => {
      const isCurrent = stageKey === ctx.currentStage?.stageKey;
      const blocked = (ctx.blockedActions || []).some((a) => a.toStageKey === stageKey);
      let status: LifecycleStageStatus = 'pending';
      if (blocked) status = 'blocked';
      else if (isCurrent && ctx.currentStage?.requiresApproval) status = 'approval_pending';
      else if (isCurrent) status = 'current';
      else if (idx < unique.indexOf(ctx.currentStage?.stageKey || '')) status = 'completed';
      return {
        stageKey,
        label: humanizeStageKey(stageKey),
        status,
        isOptional: false,
        requiresApproval: false,
      };
    });
  }

  const currentKey = ctx.currentStage?.stageKey;
  const currentOrder =
    ctx.currentStage?.sortOrder ??
    stages.find((s) => s.stageKey === currentKey)?.sortOrder ??
    null;
  const blockedKeys = new Set((ctx.blockedActions || []).map((a) => a.toStageKey));
  const lineageKeys = new Set((ctx.lineage?.entities || []).map((e) => e.stageKey));

  return stages.map((s) => {
    const isCurrent = s.stageKey === currentKey;
    const isBlocked = blockedKeys.has(s.stageKey);
    let status: LifecycleStageStatus = 'pending';

    if (isBlocked) {
      status = 'blocked';
    } else if (isCurrent && s.requiresApproval) {
      status = 'approval_pending';
    } else if (isCurrent) {
      status = 'current';
    } else if (currentOrder != null && s.sortOrder < currentOrder) {
      status = 'completed';
    } else if (lineageKeys.has(s.stageKey) && !isCurrent) {
      status = 'completed';
    } else if (s.isOptional && (currentOrder == null || s.sortOrder > currentOrder)) {
      status = 'optional';
    }

    return {
      stageKey: s.stageKey,
      label: humanizeStageKey(s.stageKey),
      status,
      isOptional: s.isOptional,
      requiresApproval: s.requiresApproval,
      entityType: s.entityType,
    };
  });
}
